import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick/lib';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { connect } from 'react-redux';
import getSlug from '../../utils/getSlug';
import getDateInProperFormat from '../../utils/getDateInProperFormat';
import PageContainer from '../common/PageContainer';
import TextDate from '../typography/TextDate';
import TextNews from '../typography/TextNews';
import TextParagraph from '../typography/TextParagraph';
import PaginationPager from '../pagination/PaginationPager';
import Button from '../common/Button';
import CarouselControls from '../common/CaruselControls';
import UkrainianCargoFlag from '../ukrainianCargoFlag/ukrainianCargoFlag';
import getPreparedSlug from '../../utils/getPreparedSlug';

const StyledFeaturedNews = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	margin: 0 auto;
	margin-top: 100px !important;
	max-width: 1440px;
	padding: 0 15px;
	@media (max-width: 600px) {
		padding: 0;
		margin: 0;
	}
	@media (min-width: 800px) {
		padding: 0 30px;
	}
	@media (min-width: 1200px) {
		flex-direction: row;
		justify-content: space-between;
		padding: 0;
	}
`;

const StyledFeaturedNewsPhotoWrapper = styled.div`
	margin: 0 auto 30px auto;
	max-width: 1000px;
	position: relative;
	width: 100%;
	@media (min-width: 1200px) {
		width: 65%;
	}
	@media (min-width: 1440px) {
		width: 960px;
	}
	.gatsby-image-wrapper {
		width: 100%;
		@media (min-width: 1440px) {
			max-height: 600px;
		}
	}

	&:after {
		border: 1px solid white;
		bottom: 0;
		content: '';
		height: calc(100% - 66px);
		left: 0;
		margin: auto;
		position: absolute;
		right: 0;
		top: 0;
		width: calc(100% - 66px);
		@media (max-width: 600px) {
			width: calc(100% - 26px);
			height: calc(100% - 26px);
		}
	}
`;

const StyledFeaturedNewsTextWrapper = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;
	height: auto;
	justify-content: center;
	width: 100%;
	@media (max-width: 600px) {
		padding: 0 15px;
	}
	@media (max-width: 1200px) {
		margin: auto;
		max-width: 1000px;
	}
	@media (min-width: 1200px) {
		width: 35%;
		padding: 0 30px 0 45px;
	}
	@media (min-width: 1440px) {
		width: 480px;
	}
`;

const StyledNewsWrapper = styled.div`
	display: block;
	margin-top: ${({ isHomePage }) => (isHomePage ? '10px' : '120px')};
	width: calc(100% + 50px);
	@media (min-width: 768px) {
		width: 100%;
	}
	@media (max-width: 600px) {
		margin-top: 0 !important;
	}

	.slick-list {
		overflow: visible;
		@media (min-width: 768px) {
			//overflow: hidden;
		}
		.slick-slider {
			margin: 0 auto;
			width: 100%;
		}
		.slick-track {
			margin-left: -15px;
			@media (max-width: 600px) {
				background-color: var(--background-grey);
			}
			@media (min-width: 768px) {
				margin-left: 0;
			}
		}
	}
`;

const StyledNewsBox = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;
	height: auto;
	justify-content: center;
	text-align: left;
	width: 100%;
	@media (max-width: 600px) {
		height: 564px;
		background-color: var(--background-grey);
	}
	.gatsby-image-wrapper {
		height: 235px;
		@media (min-width: 1200px) {
			height: 300px;
		}
	}
`;

const StyledNewsPhotoWrapper = styled.div`
	border: 1px solid ${({ theme }) => theme.colors.greyLight};
	overflow: hidden;
	padding: 10px;
	width: 100%;
`;

const StyledNewsTextWrapper = styled.div`
	margin-bottom: 25px;
	padding: 24px;
	position: relative;
`;

const StyledCardsWrapper = styled.div`
	align-items: baseline;
	display: grid;
	grid-row-gap: 40px;
	grid-template-columns: 1fr;
	width: 100%;
	@media (min-width: 600px) {
		grid-template-columns: 1fr 1fr;
		grid-row-gap: 80px;
	}
	@media (min-width: 1024px) {
		grid-template-columns: 1fr 1fr 1fr;
		grid-template-rows: 1fr;
		grid-row-gap: 110px;
		align-items: baseline;
	}
`;

const StyledButton = styled(Button)`
	position: absolute;
	top: ${({ wcagFontSizePlus }) => (wcagFontSizePlus ? '315px' : '185px')};
	@media (max-width: 1000px) {
		top: 150px;
	}
	@media (max-width: 600px) {
		position: relative;
		top: 0;
	}
`;

const News = ({
	data,
	allArticles,
	activeSite,
	isHomePage,
	isFirstPaginationPage,
	wcagFontSizePlus,
	pageContext,
	preparedSitemapSimplify,
}) => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [numberOfPages, setNumberOfPages] = useState(1);
	const sliderRef = useRef();
	const allArticlesData = allArticles?.nodes || [];
	const chooseArticleArray = data?.chooseFeaturedArticle || [];
	const featuredButtonText = chooseArticleArray?.buttonText || '';
	const featuredDescription = chooseArticleArray?.cardDescription;
	const featuredHeader = chooseArticleArray?.cardHeader || '';
	const featuredSlug = chooseArticleArray?.slug || '';
	const featuredDate = chooseArticleArray?.meta?.createdAt || '';
	const featuredImage = getImage(chooseArticleArray.cardPhoto);
	const featuredAlt = chooseArticleArray?.cardPhoto?.alt || '';
	const newFeaturedDate = getDateInProperFormat(activeSite, featuredDate);
	const globals = pageContext?.globals?.data?.globals?.nodes?.[0] || [];
	const defaultImg = getImage(globals.defaultArticleImg);
	const btnTextBack = globals?.backBtnText || '';
	const btnTextNext = globals?.nextBtnText || '';
	const ukrainianCargoFlagFirstLine = globals?.ukrainianCargoFlagFirstLine || '';
	const ukrainianCargoFlagSecondLine = globals?.ukrainianCargoFlagSecondLine || '';
	const ukrainianCargoFlagLinkId = globals?.ukrainianCargoFlagLink?.id || '';
	const preparedSlug = getPreparedSlug(ukrainianCargoFlagLinkId, preparedSitemapSimplify);
	const actionsOnResize = () => {
		const numberOfItems = allArticlesData?.length || 0;
		const windowWidth = window?.innerWidth || 0;
		const slidesPerPage = windowWidth <= 767 ? 1 : windowWidth <= 1279 ? 2 : 3;
		const calculatedNumberOfPages = isHomePage ? Math.ceil(12 / slidesPerPage) : Math.ceil(numberOfItems / slidesPerPage);
		setNumberOfPages(calculatedNumberOfPages);
	};
	useEffect(() => {
		actionsOnResize();
		window.addEventListener('resize', actionsOnResize);
		return () => window.removeEventListener('resize', actionsOnResize);
	}, []);

	const nextSlide = () => {
		sliderRef.current.slickNext();
	};

	const prevSlide = () => {
		sliderRef.current.slickPrev();
	};

	const settings = () => {
		return {
			dots: false,
			infinite: true,
			arrows: false,
			draggable: false,
			swipe: true,
			lazyLoad: true,
			slidesToShow: 3,
			initialSlide: 0,
			slidesToScroll: 3,
			centerPadding: 0,
			responsive: [
				{
					breakpoint: 3000,
					settings: {
						slidesToShow: 3,
						slidesToScroll: 3,
						initialSlide: 0,
					},
				},
				{
					breakpoint: 1279,
					settings: {
						slidesToShow: 2,
						slidesToScroll: 2,
						initialSlide: 0,
					},
				},
				{
					breakpoint: 767,
					settings: {
						slidesToShow: 1,
						slidesToScroll: 1,
						variableWidth: false,
						initialSlide: 0,
						speed: 200,
					},
				},
			],
		};
	};

	const sliderOrWrapperElement = (isHome) => {
		console.log(wcagFontSizePlus);
		if (isHome) {
			const articlesWithLimit = allArticlesData.slice(0, 12);
			return (
				<Slider ref={sliderRef} {...settings()} beforeChange={(current, next) => setCurrentSlide(next)}>
					{articlesWithLimit.map((item) => {
						const id = item?.id || '';
						const header = item?.cardHeader || '';
						const buttonText = item?.buttonText || '';
						const date = item?.meta?.createdAt || '';
						const newDate = getDateInProperFormat(activeSite, date);
						const photo = getImage(item.cardPhoto);
						const image = photo || defaultImg;
						const alt = item?.photo?.alt || '';
						const slug = item?.slug || '';

						return (
							<StyledNewsBox key={id}>
								<StyledNewsPhotoWrapper className="contrast-mode">
									<GatsbyImage image={image} alt={alt} />
								</StyledNewsPhotoWrapper>

								<StyledNewsTextWrapper>
									<TextDate className="dark-blue">{newDate}</TextDate>
									<TextNews className="dark-blue">{header}</TextNews>
									<StyledButton wcagFontSizePlus={wcagFontSizePlus} to={`/${getSlug(slug, activeSite)}`}>
										{buttonText}
									</StyledButton>
								</StyledNewsTextWrapper>
							</StyledNewsBox>
						);
					})}
				</Slider>
			);
		}
		return (
			<StyledCardsWrapper>
				{allArticlesData.map((item) => {
					const id = item?.id || '';
					const header = item?.cardHeader || '';
					const buttonText = item?.buttonText || '';
					const date = item?.meta?.createdAt || '';
					const newDate = getDateInProperFormat(activeSite, date);
					const photo = getImage(item.cardPhoto);
					const image = photo || defaultImg;
					const alt = item?.photo?.alt || '';
					const slug = item?.slug || '';

					return (
						<StyledNewsBox key={id}>
							<StyledNewsPhotoWrapper>
								<div className="contrast-mode">
									<GatsbyImage image={image} alt={alt} />
								</div>
							</StyledNewsPhotoWrapper>

							<StyledNewsTextWrapper>
								<TextDate className="dark-blue">{newDate}</TextDate>
								<TextNews className="dark-blue">{header}</TextNews>
								<StyledButton wcagFontSizePlus={wcagFontSizePlus} to={`/${getSlug(slug, activeSite)}`}>
									{buttonText}
								</StyledButton>
							</StyledNewsTextWrapper>
						</StyledNewsBox>
					);
				})}
			</StyledCardsWrapper>
		);
	};
	return (
		<PageContainer>
			{(isHomePage || isFirstPaginationPage) && (
				<StyledFeaturedNews>
					<StyledFeaturedNewsPhotoWrapper>
						<GatsbyImage className="contrast-mode" image={featuredImage} alt={featuredAlt} />
					</StyledFeaturedNewsPhotoWrapper>
					<StyledFeaturedNewsTextWrapper>
						<TextDate className="dark-blue">{newFeaturedDate}</TextDate>
						<TextNews className="featured dark-blue">{featuredHeader}</TextNews>
						<TextParagraph className="dark-blue">{featuredDescription}</TextParagraph>
						<Button to={`/${getSlug(featuredSlug, activeSite)}`} className="mt-60">
							{featuredButtonText}
						</Button>
					</StyledFeaturedNewsTextWrapper>
				</StyledFeaturedNews>
			)}
			<div className="inner-wrapper">
				{isHomePage && (
					<>
						<UkrainianCargoFlag
							firstLine={ukrainianCargoFlagFirstLine}
							secondLine={ukrainianCargoFlagSecondLine}
							preparedSlug={preparedSlug}
							activeSite={activeSite}
						/>
						<CarouselControls
							next={() => nextSlide()}
							prev={() => prevSlide()}
							btnTextBack={btnTextBack}
							numberOfPages={numberOfPages}
							currentSlide={currentSlide + 1}
							btnTextNext={btnTextNext}
						/>
					</>
				)}
				<StyledNewsWrapper isHomePage={isHomePage} className="news-animation-disabled">
					{sliderOrWrapperElement(isHomePage)}
				</StyledNewsWrapper>
			</div>
			{!isHomePage && <PaginationPager pageContext={pageContext} />}
		</PageContainer>
	);
};

export default connect((state) => ({
	wcagFontSizePlus: state.wcagFontSizePlus.wcagFontSizePlus,
}))(News);
